"use strict";
/**
 * Shared shape of the public creator application at /join.
 *
 * Frontend and backend both need the option lists (the form renders them, the
 * router validates against them), so they live here rather than being typed
 * twice and drifting.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLATFORM_PATH_SEGMENTS = exports.META_CLICK_IDENTIFIER_MAX_LENGTH = exports.CREATOR_APPLICATION_MAX_INTEREST_AREAS = exports.CREATOR_SIGNATURE_NAME_MAX_LENGTH = exports.CREATOR_APPLICATION_ABOUT_MAX_LENGTH = exports.CREATOR_APPLICATION_PORTFOLIO_URL_MAX_LENGTH = exports.CREATOR_APPLICATION_EMAIL_MAX_LENGTH = exports.CREATOR_APPLICATION_SHORT_TEXT_MAX_LENGTH = exports.CREATOR_APPLICATION_INTEREST_AREAS = exports.CREATOR_APPLICATION_BULK_REJECT_LIMIT = exports.CREATOR_APPLICATION_AGE_RANGES = exports.CREATOR_AGREEMENT_NEWFORM_SIGNATORY_TITLE = exports.CREATOR_AGREEMENT_NEWFORM_SIGNATORY_NAME = exports.CREATOR_AGREEMENT_VERSION = void 0;
exports.isCreatorAgreementDraft = isCreatorAgreementDraft;
exports.withDefaultScheme = withDefaultScheme;
exports.creatorSignatureMatchesName = creatorSignatureMatchesName;
exports.creatorSignatureIsName = creatorSignatureIsName;
exports.isMetaClickIdentifier = isMetaClickIdentifier;
/**
 * Version stamped on every signed application. Bump this whenever
 * `creatorAgreementBody.txt` changes so an applicant's row keeps pointing at
 * the text they actually accepted.
 *
 * The `-draft` suffix is load-bearing: it marks that the committed agreement
 * body has not been through legal review yet, and
 * `creatorApplicationsEnabled()` refuses to serve the public funnel while a
 * draft version is current.
 *
 * `2026-08-v2` was the counsel-approved text, generated from the Word original
 * by `npm run generate:creator-agreement-body-text`. It replaced the unreviewed
 * `2026-08-v1-draft` wholesale rather than editing it, because two staff test
 * rows already point at that version and their record must keep meaning what
 * it meant when they signed.
 *
 * `2026-10-v3` changed only the compensation summary and Section 2(a), for
 * per-brand payout rates (NEW-2971): a share of spend at the rate shown when
 * the asset was submitted, plus $100 for submissions answering an open request
 * for creative. Regenerated from the v2 Word file with those two passages
 * edited; every other line is identical.
 *
 * `2026-10-v4` makes the request $100 a minimum instead of an addition (the
 * higher of $100 or the share, NEW-2999), and says earlier submissions keep
 * the terms of the version they were submitted under, naming the original
 * $100 / 1% for anything from before per-brand rates (most of those creators
 * never signed a version). 25 people signed v3 before it changed; they sign
 * v4 before their next upload.
 */
exports.CREATOR_AGREEMENT_VERSION = "2026-10-v4";
/**
 * True while the current agreement text is an unreviewed draft.
 *
 * Trimmed and lowercased before the check: this gate is the only thing
 * standing between a deploy and collecting real signatures on unreviewed
 * contract text, so a stray trailing space or `-DRAFT` must not quietly open
 * it if the version ever comes from somewhere less controlled than the
 * literal below.
 */
function isCreatorAgreementDraft(version = exports.CREATOR_AGREEMENT_VERSION) {
    return version.trim().toLowerCase().endsWith("-draft");
}
/** NewForm signatory pre-printed on the countersigned application PDF. */
exports.CREATOR_AGREEMENT_NEWFORM_SIGNATORY_NAME = "Hamza Alsamraee";
exports.CREATOR_AGREEMENT_NEWFORM_SIGNATORY_TITLE = "CEO";
/**
 * Age bands rather than a date of birth: the program needs to know it is
 * dealing with adults and to match creators to briefs, and neither needs an
 * exact birthday on file.
 */
exports.CREATOR_APPLICATION_AGE_RANGES = [
    "18-24",
    "25-34",
    "35-44",
    "45-54",
    "55+",
];
/**
 * How many applications one "Reject selected" can take. The queue loads pages
 * of 100, so this covers a reviewer who has scrolled a couple of pages in and
 * checked everything; beyond that a second batch costs one more click, while
 * a far larger one holds the request open through hundreds of row claims.
 */
exports.CREATOR_APPLICATION_BULK_REJECT_LIMIT = 200;
/**
 * Verticals a creator says they can speak to credibly. Used at approval time
 * to match an applicant to a client program.
 */
exports.CREATOR_APPLICATION_INTEREST_AREAS = [
    "Beauty",
    "Fashion",
    "Fitness",
    "Food & beverage",
    "Finance",
    "Gaming",
    "Health & wellness",
    "Home",
    "Education",
    "Parenting",
    "Pets",
    "Sports",
    "Tech",
    "Travel",
];
/**
 * Longest name, handle, or location accepted. Mirrored onto the inputs so a
 * public form caps typing where it happens rather than rejecting a filled-in
 * application after the fact.
 */
exports.CREATOR_APPLICATION_SHORT_TEXT_MAX_LENGTH = 200;
/** RFC-ish upper bound on an address, mirrored onto the email input. */
exports.CREATOR_APPLICATION_EMAIL_MAX_LENGTH = 320;
/** Longest portfolio link accepted, mirrored onto that input. */
exports.CREATOR_APPLICATION_PORTFOLIO_URL_MAX_LENGTH = 500;
/** Longest free-text pitch accepted, so the review queue stays scannable. */
exports.CREATOR_APPLICATION_ABOUT_MAX_LENGTH = 1000;
/**
 * A portfolio link as people type it: `katieugc.com` and `www.katieugc.com`
 * mean `https://katieugc.com`, the way a browser's address bar reads them.
 * Shared so the form and the server agree on what counts as a link.
 *
 * Only a link with no scheme gets one. Anything that already names a scheme
 * (`http:`, `javascript:`, `mailto:`) is returned untouched, for the http(s)
 * check after this to accept or refuse. The dot is what separates the two:
 * a scheme never contains one, while `janedoe.com:8080` is a host and port
 * and does.
 */
function withDefaultScheme(raw) {
    var _a;
    const value = raw.trim();
    if (value.startsWith("//"))
        return `https:${value}`;
    const scheme = (_a = /^([a-z][a-z0-9+.-]*):/i.exec(value)) === null || _a === void 0 ? void 0 : _a[1];
    return scheme && !scheme.includes(".") ? value : `https://${value}`;
}
/**
 * Whether a typed electronic signature matches the name on the application.
 *
 * Shared rather than implemented on each side: the frontend checks it so the
 * applicant hears about a mismatch before submitting and the backend enforces
 * it, and two copies would drift the moment either one's normalization
 * changed (diacritics, say) — reopening the client/server disagreement this
 * whole module is careful to avoid.
 *
 * Forgiving about case, internal spacing, accents and the apostrophe or
 * dash a keyboard happens to produce (a name saved as "O’Brien" or "José"
 * must be signable from a keyboard that types "O'Brien" and "Jose"), strict
 * about the actual letters: the point is an affirmative act tied to the
 * identity on record, not a typing test.
 */
function creatorSignatureMatchesName(signatureName, name) {
    const normalize = (value) => value
        .normalize("NFKD")
        .replace(/\p{M}/gu, "")
        .replace(/[\u2018\u2019\u02BC\u0060\u00B4]/g, "'")
        .replace(/[\u2010-\u2015\u2212]/g, "-")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
    return normalize(signatureName) === normalize(name);
}
exports.CREATOR_SIGNATURE_NAME_MAX_LENGTH = 200;
/**
 * Whether a typed electronic signature is a name at all, for signing where
 * there is no name on the same form to match it against: the portal, where an
 * existing creator signs a revised agreement. They are already signed in, and
 * the name on their profile was typed by staff, so it can be a typo, a
 * nickname or a handle; requiring a match against it blocked real names
 * ("Andrew Frazier" against a profile saying "Andrew Fraizer"). The typed name
 * is the signature and is recorded as given.
 */
function creatorSignatureIsName(signatureName) {
    const value = signatureName.trim();
    return (value.length >= 2 &&
        value.length <= exports.CREATOR_SIGNATURE_NAME_MAX_LENGTH &&
        /\p{L}/u.test(value));
}
/** At least one interest area, capped so the answer stays a signal. */
exports.CREATOR_APPLICATION_MAX_INTEREST_AREAS = 5;
/**
 * Longest `_fbp` / `_fbc` value accepted from the application form.
 *
 * Generous next to the ~60 characters Meta actually produces, because the
 * click id inside `_fbc` is opaque and has grown before. The cap is here to
 * stop the public endpoint storing an unbounded string, not to police the
 * format — `isMetaClickIdentifier` does that.
 */
exports.META_CLICK_IDENTIFIER_MAX_LENGTH = 512;
/**
 * Whether a value looks like one of Meta's browser click identifiers.
 *
 * Both cookies share the shape `fb.<subdomainIndex>.<creationTimeMs>.<id>` —
 * a random number for `_fbp`, the `fbclid` from the ad click for `_fbc`. The
 * last segment is matched loosely on purpose: it is an opaque token Meta
 * defines, and a regex that tracked its current alphabet exactly would start
 * silently dropping real click attribution the first time Meta widened it.
 *
 * Shared because both sides need the same answer for different reasons: the
 * form uses it to avoid posting a cookie some other script wrote, and the
 * router uses it to keep unvalidated public input out of a Conversions API
 * payload. A second copy would drift, and the failure would be invisible —
 * an identifier quietly dropped reads exactly like a creator who never
 * clicked an ad.
 */
function isMetaClickIdentifier(value) {
    return (value.length <= exports.META_CLICK_IDENTIFIER_MAX_LENGTH &&
        /^fb\.\d+\.\d+\.[A-Za-z0-9._-]+$/.test(value));
}
/**
 * Paths that belong to the platform rather than to a person: the segments a
 * share sheet appends after a handle, and the section names a URL can have in
 * the handle's own position.
 *
 * Both uses need the same list. After the handle, only these are stripped, so
 * `someone/profilecard` reduces to `someone` while `N/A` is left intact to be
 * refused as the non-handle it is — truncating at any slash turned that into a
 * plausible-looking `N`, which nobody would think to fix. In the handle's
 * position they are refused outright by `sanitizeCreatorHandle` and the
 * editors: `instagram.com/reels/` is a link to a section of Instagram, and
 * storing `reels` as somebody's account points every later partnership
 * request at nothing. No platform lets anyone register these as a username.
 */
exports.PLATFORM_PATH_SEGMENTS = new Set([
    "about",
    "accounts",
    "direct",
    "discover",
    "explore",
    "followers",
    "following",
    "foryou",
    "live",
    "messages",
    "music",
    "p",
    "profilecard",
    "reel",
    "reels",
    "search",
    "story",
    "stories",
    "tag",
    "tv",
    "upload",
    "video",
    "videos",
]);
